import {
  FIRST_PAGE, blankRow, compileRow, headerRow, pageToText, resolveLinks, subpageRows,
  type Link, type Row,
} from '@textbox/core';
import { DEFAULT_CONFIG, Navigator, type FastextKey, type NavConfig } from './nav.ts';
import { ScreenRenderer, describeLink, fitScreen } from '@textbox/renderer';
import '@textbox/renderer/screen.css';
import { JsonPageSource } from './source.ts';
import './styles.css';

const SERVICE = 'TEXTBOX';

// ---- config (URL overrides exist for the browser test-suite) ----------
const params = new URLSearchParams(location.search);
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const config: NavConfig = { ...DEFAULT_CONFIG, reducedMotion };
if (params.get('fast') === '1') {
  Object.assign(config, { searchMinMs: 150, searchMaxMs: 250, notFoundMs: 700, rollMs: 40, rotateMs: 1500, pollMs: 0 });
}
const num = (k: string) => (params.has(k) ? Number(params.get(k)) : undefined);
config.rotateMs = num('rotate') ?? config.rotateMs;
config.pollMs = num('poll') ?? config.pollMs;

// ---- elements ------------------------------------------------------------
const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const screenEl = $('screen');
const capPage = $('cap-page');
const capLink = $('cap-link');
const pageText = $<HTMLPreElement>('page-text');
const stateEl = $('state');
const keypad = $<HTMLDialogElement>('keypad');
const keypadEntry = $('keypad-entry');

const source = new JsonPageSource(params.get('pages') ?? '/pages', params.get('events') === '0' ? null : '/events');
const nav = new Navigator(source, config);
const renderer = new ScreenRenderer(screenEl);

let links: Link[] = [];
let hover: Link | null = null;
let notInServiceRows: Row[] | null = null;

function notInService(page: number): Row[] {
  const rows: Row[] = [];
  for (let r = 0; r < 25; r++) rows.push(blankRow());
  rows[10] = compileRow(`[R]   PAGE ${page} NOT IN SERVICE`);
  rows[12] = compileRow('[W]   CLICK INDEX OR TYPE A NUMBER');
  return rows;
}
/** The word INDEX on the not-in-service page is a click target for page 100. */
const NOT_IN_SERVICE_LINKS: Link[] = [{ row: 12, colStart: 10, colEnd: 15, target: { page: 100 }, origin: 'map' }];

function currentRows(): Row[] {
  if (nav.current) return subpageRows(nav.current);
  if (nav.state.notInService && nav.state.shown) return notInService(nav.state.shown);
  return notInServiceRows ?? (notInServiceRows = Array.from({ length: 25 }, () => blankRow()));
}

function render(): void {
  const s = nav.state;
  const rows = currentRows();
  rows[0] = headerRow({
    service: SERVICE,
    mode: s.mode,
    page: s.mode === 'searching' ? s.searchNum : (s.shown ?? FIRST_PAGE),
    entry: s.entry,
    sub: s.sub,
    subCount: s.subCount,
    now: new Date(),
  });
  screenEl.classList.toggle('mix', s.mix);
  renderer.render(rows, {
    reveal: s.reveal,
    mix: s.mix,
    links,
    hover,
    addressColour: s.mode === 'searching' ? 2 : 7,
    enhancements: nav.current?.enhancements,
  });
}

function updateChrome(): void {
  const s = nav.state;
  const sp = nav.current;
  if (sp) {
    const title = sp.meta.title ? ` · ${sp.meta.title}` : '';
    capPage.innerHTML = `<b>Page ${sp.page}</b>${sp.subCount > 1 ? ` · subpage ${sp.sub} of ${sp.subCount}` : ''}${title}`;
    pageText.textContent = `Page ${sp.page}${sp.subCount > 1 ? `, subpage ${sp.sub} of ${sp.subCount}` : ''}\n` + pageToText(subpageRows(sp), { reveal: true });
    screenEl.setAttribute('aria-label', `Teletext page ${sp.page}${sp.meta.title ? `: ${sp.meta.title}` : ''}`);
  } else if (s.notInService && s.shown) {
    capPage.innerHTML = `<b>Page ${s.shown}</b> not in service`;
    pageText.textContent = `Page ${s.shown} not in service`;
    screenEl.setAttribute('aria-label', `Page ${s.shown} not in service`);
  } else {
    capPage.textContent = '';
  }
  const labels = { idle: 'Showing page', entering: 'Entering number', searching: 'Searching…' } as const;
  const extra: string[] = [];
  if (s.hold) extra.push('hold');
  if (s.reveal) extra.push('reveal');
  if (s.mix) extra.push('mix');
  if (sp && sp.subCount > 1 && !s.hold && s.mode === 'idle') extra.push('rotating subpages');
  stateEl.innerHTML = `${s.mode.toUpperCase()}<small>${labels[s.mode]}${s.mode === 'entering' ? ' ' + s.entry : ''}${s.mode === 'searching' && s.target ? ' ' + s.target : ''}${extra.length ? ' · ' + extra.join(', ') : ''}</small>`;
  $('btn-hold').setAttribute('aria-pressed', String(s.hold));
  $('btn-reveal').setAttribute('aria-pressed', String(s.reveal));
  $('btn-mix').setAttribute('aria-pressed', String(s.mix));
  keypadEntry.textContent = 'P' + s.entry.padEnd(3, '.');
}

function updateCaptionLink(): void {
  if (!hover) { capLink.textContent = ''; return; }
  if ('url' in hover.target) { capLink.textContent = `Opens ${hover.target.url}`; return; }
  const title = nav.titleOf(hover.target.page);
  capLink.textContent = `${hover.origin === 'fastext' ? 'Fastext' : 'Link'} → ${hover.target.page}${title ? ' ' + title : ''}`;
}

nav.subscribe((s, sp, reason) => {
  if (reason === 'page' || reason === 'sub' || reason === 'refresh') {
    links = sp ? resolveLinks(sp, subpageRows(sp)) : s.notInService ? NOT_IN_SERVICE_LINKS : [];
    hover = null;
    renderer.setHover(null);
    updateCaptionLink();
    void s;
  }
  render();
  updateChrome();
});

// ---- URL hash ⇄ page --------------------------------------------------
function parseHash(): { page: number; sub: number } | null {
  const m = /^#(\d{3})(?:\/(\d+))?$/.exec(location.hash);
  return m ? { page: Number(m[1]), sub: Number(m[2] ?? 1) } : null;
}
nav.setNavigateHandler((page, sub, replace) => {
  const hash = `#${page}${sub > 1 ? `/${sub}` : ''}`;
  if (location.hash === hash) return;
  if (replace) history.replaceState(null, '', hash);
  else history.pushState(null, '', hash);
});
window.addEventListener('popstate', () => {
  const h = parseHash();
  if (!h) return;
  if (h.page !== nav.state.shown || h.sub !== nav.state.sub) void nav.goto(h.page, h.sub);
});
window.addEventListener('hashchange', () => {
  const h = parseHash();
  if (h && (h.page !== nav.state.shown || h.sub !== nav.state.sub)) void nav.goto(h.page, h.sub);
});

// ---- pointer navigation over the grid ---------------------------------
function activate(link: Link): void {
  if ('url' in link.target) { window.open(link.target.url, '_blank', 'noopener'); return; }
  void nav.goto(link.target.page, link.target.sub ?? 1);
}
screenEl.addEventListener('mouseover', (e) => {
  const hit = renderer.hitTest(e.target);
  const link = hit?.link ?? null;
  if (renderer.setHover(link)) { hover = link; updateCaptionLink(); render(); }
});
screenEl.addEventListener('mouseleave', () => {
  if (renderer.setHover(null)) { hover = null; updateCaptionLink(); render(); }
});
screenEl.addEventListener('click', (e) => {
  const hit = renderer.hitTest(e.target);
  if (!hit) return;
  if (hit.row === 0 && hit.col < 8) { openKeypad(); return; }
  if (hit.link) activate(hit.link);
});
screenEl.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && hover) { activate(hover); e.preventDefault(); }
});

// long-press tooltip (touch has no title tooltips)
let pressTimer: ReturnType<typeof setTimeout> | null = null;
let tip: HTMLElement | null = null;
screenEl.addEventListener('touchstart', (e) => {
  const hit = renderer.hitTest(e.target);
  if (!hit?.link) return;
  const link = hit.link;
  const t = e.touches[0]!;
  pressTimer = setTimeout(() => {
    tip = document.createElement('div');
    tip.className = 'tooltip';
    tip.textContent = describeLink(link);
    tip.style.left = `${t.clientX}px`;
    tip.style.top = `${t.clientY}px`;
    document.body.appendChild(tip);
  }, 500);
}, { passive: true });
const endPress = () => { if (pressTimer) clearTimeout(pressTimer); pressTimer = null; if (tip) { tip.remove(); tip = null; } };
screenEl.addEventListener('touchend', endPress);
screenEl.addEventListener('touchcancel', endPress);
screenEl.addEventListener('touchmove', endPress, { passive: true });
screenEl.addEventListener('contextmenu', (e) => {
  const hit = renderer.hitTest(e.target);
  if (hit?.link) { e.preventDefault(); endPress(); tip = document.createElement('div'); tip.className = 'tooltip'; tip.textContent = describeLink(hit.link); tip.style.left = `${e.clientX}px`; tip.style.top = `${e.clientY}px`; document.body.appendChild(tip); setTimeout(endPress, 1500); }
});

// ---- remote + keypad ---------------------------------------------------
function buildPad(container: HTMLElement): void {
  for (const d of [1, 2, 3, 4, 5, 6, 7, 8, 9, '', 0, '']) {
    const b = document.createElement('button');
    b.className = 'key';
    b.type = 'button';
    if (d === '') { b.style.visibility = 'hidden'; b.tabIndex = -1; b.setAttribute('aria-hidden', 'true'); }
    else { b.textContent = String(d); b.dataset.digit = String(d); b.addEventListener('click', () => nav.digit(String(d))); }
    container.appendChild(b);
  }
}
buildPad($('pad'));
buildPad($('keypad-pad'));
function openKeypad(): void {
  keypad.showModal();
}
$('keypad-clear').addEventListener('click', () => nav.escape());
$('keypad-close').addEventListener('click', () => keypad.close());
nav.subscribe((s) => { if (keypad.open && s.mode === 'searching') keypad.close(); });

document.querySelectorAll<HTMLButtonElement>('[data-ft]').forEach((b) => b.addEventListener('click', () => nav.fastext(b.dataset.ft as FastextKey)));
$('btn-prev').addEventListener('click', () => void nav.stepSub(-1));
$('btn-next').addEventListener('click', () => void nav.stepSub(1));
$('btn-hold').addEventListener('click', () => nav.toggleHold());
$('btn-reveal').addEventListener('click', () => nav.toggleReveal());
$('btn-mix').addEventListener('click', () => nav.toggleMix());
$('btn-index').addEventListener('click', () => nav.index());

document.addEventListener('keydown', (e) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
  const k = e.key;
  if (/^[0-9]$/.test(k)) nav.digit(k);
  else if (k === 'r' || k === 'R') nav.fastext('red');
  else if (k === 'g' || k === 'G') nav.fastext('green');
  else if (k === 'y' || k === 'Y') nav.fastext('yellow');
  else if (k === 'b' || k === 'B') nav.fastext('blue');
  else if (k === 'ArrowLeft') void nav.stepSub(-1);
  else if (k === 'ArrowRight') void nav.stepSub(1);
  else if (k === 'h' || k === 'H') nav.toggleHold();
  else if (k === 'v' || k === 'V') nav.toggleReveal();
  else if (k === 'm' || k === 'M') nav.toggleMix();
  else if (k === 'i' || k === 'I') nav.index();
  else if (k === 'Escape') { if (keypad.open) keypad.close(); nav.escape(); }
  else if (k === 'Backspace') nav.backspace();
  else return;
  e.preventDefault();
});

// ---- pixel-exact sizing ---------------------------------------------------
/**
 * Size the screen so each cell (and each sixel block) is a whole number of
 * device pixels: no anti-aliased seams between adjacent cells. Cells keep
 * the 5:6 teletext aspect; the text stretch factor follows from that.
 */
const wrap = $('screen-wrap');
const refit = () => fitScreen(screenEl, wrap.clientWidth, wrap.clientHeight);
// Debug chrome (caption + remote) only when asked for.
const withChrome = params.get('remote') === '1';
$('chrome').hidden = !withChrome;
document.body.classList.toggle('with-chrome', withChrome);
new ResizeObserver(refit).observe(wrap);
window.addEventListener('resize', refit);
refit();

// ---- clock + boot --------------------------------------------------------
setInterval(() => { if (nav.state.mode !== 'searching') render(); }, 1000);
render();
const initial = parseHash();
void nav.start(initial?.page ?? FIRST_PAGE, initial?.sub ?? 1);

// expose for the browser tests
declare global { interface Window { textbox: { nav: Navigator } } }
window.textbox = { nav };
